import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Wrench, User } from "lucide-react";
import axios from "axios";
import ImportExportButtons from "../../components/ImportExportButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MechanicsTab() {
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMechanic, setEditingMechanic] = useState(null);
  const [newMechanic, setNewMechanic] = useState({ name: "", username: "", password: "" });

  useEffect(() => {
    fetchMechanics();
  }, []);

  const fetchMechanics = async () => {
    try {
      const res = await axios.get(`${API}/mechanics`);
      setMechanics(res.data || []);
    } catch (error) {
      console.error("Error fetching mechanics:", error);
      toast.error("Failed to load mechanics");
    } finally {
      setLoading(false);
    }
  };

  const filteredMechanics = mechanics.filter(m =>
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/mechanics`, newMechanic);
      toast.success("Mechanic created successfully!");
      setShowAddModal(false);
      setNewMechanic({ name: "", username: "", password: "" });
      fetchMechanics();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create mechanic");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (editingMechanic.name) updateData.name = editingMechanic.name;
      if (editingMechanic.username) updateData.username = editingMechanic.username;
      if (editingMechanic.newPassword) updateData.password = editingMechanic.newPassword;
      
      await axios.patch(`${API}/admin/mechanics/${editingMechanic.id}`, updateData);
      toast.success("Mechanic updated successfully!");
      setEditingMechanic(null);
      fetchMechanics();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update mechanic");
    }
  };

  const handleDelete = async (mechanicId) => {
    if (!window.confirm("Are you sure you want to delete this mechanic?")) return;
    try {
      await axios.delete(`${API}/admin/mechanics/${mechanicId}`);
      toast.success("Mechanic deleted successfully!");
      fetchMechanics();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete mechanic");
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
              Mechanics
            </h2>
            <p className="text-white/60 text-sm mt-1">{mechanics.length} total mechanics</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <ImportExportButtons
              entityName="Mechanics"
              exportEndpoint="/admin/mechanics/export"
              importEndpoint="/admin/mechanics/import"
              templateEndpoint="/admin/mechanics/template"
              onImportSuccess={fetchMechanics}
              exportFileName="mechanics"
            />
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap flex-1 sm:flex-none"
              data-testid="add-mechanic-btn"
            >
              <Plus className="w-4 h-4" />
              <span>Add Mechanic</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          type="text"
          placeholder="Search mechanics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-orange-500"
          data-testid="mechanic-search"
        />
      </div>

      {/* Mechanics Table */}
      <div className="bg-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="mechanics-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/60 font-medium">Name</th>
                <th className="text-left p-4 text-white/60 font-medium">Username</th>
                <th className="text-left p-4 text-white/60 font-medium">Password</th>
                <th className="text-right p-4 text-white/60 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMechanics.map((mechanic) => (
                <tr key={mechanic.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 text-white">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-orange-500" />
                      {mechanic.name || mechanic.full_name}
                    </div>
                  </td>
                  <td className="p-4 text-white/80">{mechanic.username}</td>
                  <td className="p-4 text-white/60 font-mono text-sm">{mechanic.plain_password || "••••••"}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingMechanic({ ...mechanic, newPassword: "" })}
                        className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                        data-testid={`edit-mechanic-${mechanic.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(mechanic.id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        data-testid={`delete-mechanic-${mechanic.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredMechanics.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-white/40">
                    No mechanics found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-md border border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">Add Mechanic</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={newMechanic.name}
                  onChange={(e) => setNewMechanic({ ...newMechanic, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                  data-testid="new-mechanic-name"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Username</label>
                <input
                  type="text"
                  value={newMechanic.username}
                  onChange={(e) => setNewMechanic({ ...newMechanic, username: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                  data-testid="new-mechanic-username"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Password</label>
                <input
                  type="text"
                  value={newMechanic.password}
                  onChange={(e) => setNewMechanic({ ...newMechanic, password: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                  data-testid="new-mechanic-password"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  data-testid="submit-new-mechanic"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingMechanic && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-md border border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">Edit Mechanic</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={editingMechanic.name || editingMechanic.full_name || ""}
                  onChange={(e) => setEditingMechanic({ ...editingMechanic, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="edit-mechanic-name"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Username</label>
                <input
                  type="text"
                  value={editingMechanic.username}
                  onChange={(e) => setEditingMechanic({ ...editingMechanic, username: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="edit-mechanic-username"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">New Password (leave blank to keep current)</label>
                <input
                  type="text"
                  value={editingMechanic.newPassword || ""}
                  onChange={(e) => setEditingMechanic({ ...editingMechanic, newPassword: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="edit-mechanic-password"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingMechanic(null)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  data-testid="submit-edit-mechanic"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
